import React, { useState } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Button,
  CircularProgress,
  Alert,
  Typography,
  Box
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import AppSettingsAltIcon from '@mui/icons-material/AppSettingsAlt';


// IMPORTANTE: Ajusta esta ruta según la ubicación real de tu slice
import { useGetClientesVencidosDetalleQuery } from '../../../store/api/endpoints/reportesApiSlice';
import ButtonIcon from '../../elementos/ButtonIcon';

const ClientesVencidos = () => {
  // 1. Obtener el vendedorId de la URL (?vendedorId=abc123)
  const [searchParams] = useSearchParams();
  const vendedorId = searchParams.get('vendedorId');

  // 2. Estados locales para la paginación de MUI (MUI usa base 0 para las páginas)
  const [page, setPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // 3. Ejecutar la query de RTK Query
  // Sumamos 1 a `page` porque tu API espera que la primera página sea 1
  const { data, error, isLoading, isFetching } = useGetClientesVencidosDetalleQuery(
    {
      vendedorId: vendedorId || '',
      page: page + 1,
      itemsPerPage
    },
    {
      // Opcional: no hacer la petición si no hay vendedorId
      skip: !vendedorId
    }
  );

  // 4. Manejadores de paginación de MUI
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setItemsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Volver a la primera página al cambiar la cantidad de items
  };

  // 5. Renderizados condicionales (Carga, Error, Falta de ID)
  if (!vendedorId) {
    return <Alert severity="warning">No se especificó un vendedorId en la URL.</Alert>;
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Ocurrió un error al cargar los clientes.</Alert>;
  }

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden', position: 'relative' }}>

      {/* Indicador visual si estamos trayendo datos nuevos por cambio de página */}
      {isFetching && (
        <CircularProgress
          size={24}
          sx={{ position: 'absolute', top: 16, right: 16, zIndex: 10 }}
        />
      )}

      <TableContainer >
        <Table stickyHeader aria-label="tabla de clientes vencidos">
          <TableHead>
            <TableRow>
              <TableCell><strong>Dominio</strong></TableCell>
              <TableCell><strong>Período Cubierto Hasta</strong></TableCell>
              <TableCell><strong>Fecha Último Pago</strong></TableCell>
              <TableCell align="center"><strong>Meses Adeudados</strong></TableCell>
              <TableCell align="center"><strong>Acciones</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.datos?.length > 0 ? (
              data.datos.map((row) => (
                <TableRow hover key={row.dominio}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="500">
                      {row.dominio}
                    </Typography>
                  </TableCell>
                  <TableCell>{row.periodo_cubierto_hasta}</TableCell>
                  <TableCell>{row.fecha_ultimo_pago}</TableCell>
                  <TableCell align="center">
                    <Typography
                      variant="body2"
                      color={row.meses_adeudados > 0 ? 'error.main' : 'success.main'}
                      fontWeight="bold"
                    >
                      {row.meses_adeudados}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <ButtonIcon
                      variant="outlined"
                      size="small"
                      startIcon={<SearchIcon />}
                      component={RouterLink}
                      to={`/clientes/${row.mongoId}`}
                      disableElevation
                      marginRight
                    />
                    <ButtonIcon
                      variant="outlined"
                      size="small"
                      color="success"
                      startIcon={<PointOfSaleIcon />}
                      to={'/clientes/' + row.mongoId + '/cuenta-corriente'}
                      marginRight
                    />
                    <ButtonIcon
                      variant="outlined"
                      size="small"
                      color="secondary"
                      startIcon={<AppSettingsAltIcon />}
                      to={'/clientes/' + row.mongoId + '/app'}
                    />
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No se encontraron clientes vencidos.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginación */}
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={data?.totalItems || 0} // Total real desde la API
        rowsPerPage={itemsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Filas por página:"
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count !== -1 ? count : `más de ${to}`}`}
      />
    </Paper>
  );
};

export default ClientesVencidos;